import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { InferType } from 'yup';

import { useAuthStore } from './auth.store';
import { AuthService } from './auth.service';
import type {
  forgotSchema,
  loginSchema,
  resetPasswordSchema,
  setPasswordSchema,
} from './auth.validation';
import { useFilterStore } from '@/stores/filter.store';
import { forgetListFilters } from '@/hooks/use-url-filters';
import { useCompanyStore } from '@/stores/company.store';
import {
  clearPendingChallenge,
  clearPendingPasswordSetup,
  getTrustedDeviceToken,
  removeTrustedDeviceToken,
  saveTrustedDeviceToken,
  savePendingChallenge,
  savePendingPasswordSetup,
  type PendingChallenge,
  type PendingPasswordSetup,
} from './two-factor.storage';
import type {
  IExportChallenge,
  ITotpChallenge,
  ITotpSetupChallenge,
  ITwoFactorChallenge,
} from './auth.types';
import { useExportTokenStore } from './export-confirmation';
import { clearViewAs, readViewAs } from './view-as';
import { useTotpLoginStore } from './totp-login';

const TWO_FACTOR_PATH = '/verify-identity';
const SET_PASSWORD_PATH = '/set-password';
const TOTP_VERIFY_PATH = '/authenticator';
const TOTP_SETUP_PATH = '/authenticator/setup';

// A login answered with an email code — from the login form, or right after
// the user set their own password — continues on the verify page.
const useStartTwoFactor = () => {
  const navigate = useNavigate();

  return (
    result: ITwoFactorChallenge,
    email: string,
    trustedDeviceToken?: string
  ) => {
    // A token we sent and still got asked for a code was expired or revoked
    // (password change, 2FA turned off); stop sending it.
    if (trustedDeviceToken) removeTrustedDeviceToken(email);

    const challenge: PendingChallenge = {
      challengeId: result.challenge_id,
      email,
      emailMasked: result.email_masked,
      sentAt: Date.now(),
      resendAfter: result.resend_after,
    };
    savePendingChallenge(challenge);
    navigate(TWO_FACTOR_PATH, { replace: true, state: challenge });
  };
};

// A login answered with "use your authenticator app" (or "set one up first"),
// from the login form or right after the user set their own password.
const useStartTotp = () => {
  const navigate = useNavigate();
  const startChallenge = useTotpLoginStore(s => s.startChallenge);
  const startSetup = useTotpLoginStore(s => s.startSetup);

  return (result: ITotpChallenge | ITotpSetupChallenge) => {
    if ('requires_totp_setup' in result) {
      startSetup(result.setup_token, result.expires_in);
      navigate(TOTP_SETUP_PATH, { replace: true });
      return;
    }

    startChallenge(result.challenge_id, result.expires_in);
    navigate(TOTP_VERIFY_PATH, { replace: true });
  };
};

/**
 * Forgets everything the last operator left in memory: cached data, page
 * filters and the selected tenant. Safe only where no CMS page is mounted
 * (the login screens, or the moment a login completes): under a mounted page
 * it makes that page's queries refetch.
 */
const useResetSessionState = () => {
  const queryClient = useQueryClient();
  const resetFilter = useFilterStore(s => s.reset);
  const resetCompany = useCompanyStore(s => s.reset);

  return useCallback(() => {
    queryClient.clear();
    resetFilter();
    forgetListFilters();
    resetCompany();
    clearViewAs();
  }, [queryClient, resetFilter, resetCompany]);
};

// The last step of every successful login, with or without the email code.
const useCompleteLogin = () => {
  const navigate = useNavigate();
  const setToken = useAuthStore(state => state.setToken);
  const resetSessionState = useResetSessionState();

  return (accessToken: string | undefined, message?: string) => {
    if (!accessToken) {
      toast.error('Something went wrong');
      return;
    }

    // The login screens already did this on mount; repeated here for any
    // future way into a login that skips them.
    resetSessionState();
    setToken?.(accessToken);
    toast.success(message);
    navigate('/today', { replace: true });
  };
};

const useAuth = () => {
  const [hidePassword, setHidePassword] = useState<boolean>(true);
  const [hideNewPassword, setHideNewPassword] = useState<boolean>(true);
  const [hideConfirmationPassword, setHideConfirmationPassword] =
    useState<boolean>(true);

  const navigate = useNavigate();
  const completeLogin = useCompleteLogin();
  const startTwoFactor = useStartTwoFactor();
  const startTotp = useStartTotp();

  const removeToken = useAuthStore(state => state.removeToken);
  const queryClient = useQueryClient();

  const loginMutation = useMutation({ mutationFn: AuthService.login });
  const logoutMutation = useMutation({ mutationFn: AuthService.logout });
  const forgotMutation = useMutation({
    mutationFn: AuthService.forgotPassword,
  });
  const resetMutation = useMutation({ mutationFn: AuthService.resetPassword });

  const togglePassword = () => {
    setHidePassword(prev => !prev);
  };

  const toggleNewPassword = () => {
    setHideNewPassword(prev => !prev);
  };

  const toggleConfirmationPassword = () => {
    setHideConfirmationPassword(prev => !prev);
  };

  const login = (data: InferType<typeof loginSchema>) => {
    if (loginMutation.isPending) return;

    const trustedDeviceToken = getTrustedDeviceToken(data.email);

    loginMutation.mutate(
      { ...data, trusted_device_token: trustedDeviceToken },
      {
        onSuccess: response => {
          const result = response?.data;

          // Checked in the order the API guide gives: a token first, then
          // each kind of challenge.
          if (result && 'access_token' in result) {
            completeLogin(result.access_token, response?.meta.message);
            return;
          }

          // Still on the temporary password from the new-account email: the
          // user picks their own first, and the login carries on from there.
          if (result && 'requires_password_change' in result) {
            savePendingPasswordSetup({
              token: result.password_change_token,
              email: data.email,
            });
            navigate(SET_PASSWORD_PATH);
            return;
          }

          if (result && 'requires_totp_setup' in result) {
            startTotp(result);
            return;
          }

          if (result && 'requires_totp' in result) {
            startTotp(result);
            return;
          }

          if (result && 'requires_2fa' in result) {
            startTwoFactor(result, data.email, trustedDeviceToken);
            return;
          }

          completeLogin(undefined);
        },
        onError: error => {
          toast.error(error?.message ?? 'Something went wrong');
        },
      }
    );
  };

  // Ends the session locally whatever the API answered. The token goes first
  // so the login route's loader does not verify a revoked one. Nothing else
  // is touched here: the CMS page is still mounted until the navigation
  // renders, and changing a store under it refetches its queries with the
  // dead token, whose 401 raises a toast and a full reload from the axios
  // interceptor. The login screen clears the cache and the stores once it
  // has mounted, when the CMS page is gone (`BaseLayout`).
  const endSession = async () => {
    removeToken?.();
    await queryClient.cancelQueries();
    await navigate('/', { replace: true });
  };

  const logout = () => {
    if (logoutMutation.isPending) return;

    // A running view-as ends with the Admin: its own token, named, then the
    // Admin's, which the interceptor sends once the view-as is gone.
    const viewAs = readViewAs();
    if (viewAs) {
      void AuthService.logout(viewAs.token).catch(() => undefined);
      clearViewAs();
    }

    logoutMutation.mutate(undefined, {
      onSettled: () => {
        void endSession();
      },
    });
  };

  const forgotPassword = (data: InferType<typeof forgotSchema>) => {
    forgotMutation.mutate(data, {
      onSuccess: () => {
        navigate(-1);
        toast.success('Check your email for the password reset link.');
      },
      onError: error => {
        toast.error(error?.message ?? 'Something went wrong');
      },
    });
  };

  const resetPassword = (data: InferType<typeof resetPasswordSchema>) => {
    resetMutation.mutate(data, {
      onSuccess: response => {
        toast.success(response.meta.message);
        navigate('/', { replace: true });
      },
      onError: error => {
        toast.error(error?.message ?? 'Something went wrong');
      },
    });
  };

  return {
    hidePassword,
    togglePassword,
    hideNewPassword,
    toggleNewPassword,
    hideConfirmationPassword,
    toggleConfirmationPassword,
    loginMutation,
    logoutMutation,
    forgotMutation,
    resetMutation,
    login,
    logout,
    forgotPassword,
    resetPassword,
  };
};

// Error body of backend-auth: { status, success, message }.
type AuthError = { status?: number; message?: string } | null;

// 403 is an account suspended after too many wrong codes. An expired
// challenge or one out of resends asks to log in again. Anything else — a
// wrong code, the resend cooldown — keeps the user on the verify page.
const isSuspended = (error: AuthError) => error?.status === 403;
const endsChallenge = (error: AuthError) =>
  isSuspended(error) || /log in again/i.test(error?.message ?? '');

const showChallengeError = (error: AuthError) => {
  toast.error(error?.message ?? 'Something went wrong', {
    // Long enough to read "contact your administrator" after the page changes.
    duration: isSuspended(error) ? 10000 : undefined,
  });
};

const useTwoFactor = (challenge: PendingChallenge | null) => {
  const navigate = useNavigate();
  const completeLogin = useCompleteLogin();

  const verifyMutation = useMutation({
    mutationFn: AuthService.verifyTwoFactor,
  });
  const resendMutation = useMutation({
    mutationFn: AuthService.resendTwoFactor,
  });

  const backToLogin = () => {
    clearPendingChallenge();
    navigate('/', { replace: true });
  };

  const verify = (code: string, rememberDevice: boolean) => {
    if (!challenge || verifyMutation.isPending) return;

    verifyMutation.mutate(
      {
        challenge_id: challenge.challengeId,
        code,
        remember_device: rememberDevice,
      },
      {
        onSuccess: response => {
          const trustedDeviceToken = response?.data?.trusted_device_token;
          if (trustedDeviceToken) {
            saveTrustedDeviceToken(challenge.email, trustedDeviceToken);
          }

          clearPendingChallenge();
          completeLogin(response?.data?.access_token, response?.meta.message);
        },
        onError: (error: AuthError) => {
          showChallengeError(error);
          if (endsChallenge(error)) backToLogin();
        },
      }
    );
  };

  const resend = (onSent: (next: PendingChallenge) => void) => {
    if (!challenge || resendMutation.isPending) return;

    resendMutation.mutate(
      { challenge_id: challenge.challengeId },
      {
        onSuccess: response => {
          const next: PendingChallenge = {
            ...challenge,
            sentAt: Date.now(),
            resendAfter: response?.data?.resend_after ?? challenge.resendAfter,
          };
          savePendingChallenge(next);
          onSent(next);
          toast.success(response?.meta.message);
        },
        onError: (error: AuthError) => {
          showChallengeError(error);
          if (endsChallenge(error)) backToLogin();
        },
      }
    );
  };

  return { verify, resend, backToLogin, verifyMutation, resendMutation };
};

// A code sent to confirm a CSV export. Lives in memory only.
type ExportChallenge = {
  challengeId: string;
  emailMasked: string;
  sentAt: number;
  resendAfter: number;
};

const toExportChallenge = (data: IExportChallenge): ExportChallenge => ({
  challengeId: data.challenge_id,
  emailMasked: data.email_masked,
  sentAt: Date.now(),
  resendAfter: data.resend_after,
});

// The export endpoints are exempt from the axios 401 redirect, because there
// a 401 is usually a wrong or expired code. This is the one that is not.
const isSessionExpired = (error: AuthError) =>
  error?.status === 401 && /token not valid/i.test(error.message ?? '');

/**
 * The email code in front of a booking CSV export. `confirm(run)` asks for a
 * code and calls `run` with the export token once the user enters it; the
 * token is kept (in memory) so exports in the next minutes skip the code.
 */
const useExportConfirmation = () => {
  const { logout } = useAuth();
  const saveToken = useExportTokenStore(s => s.save);
  const [challenge, setChallenge] = useState<ExportChallenge | null>(null);
  const [error, setError] = useState<string>();
  const pendingRun = useRef<((token: string) => void) | undefined>(undefined);

  const startMutation = useMutation({
    mutationFn: AuthService.startExportConfirmation,
  });
  const verifyMutation = useMutation({
    mutationFn: AuthService.verifyExportConfirmation,
  });
  const resendMutation = useMutation({
    mutationFn: AuthService.resendExportConfirmation,
  });

  const close = () => {
    setChallenge(null);
    setError(undefined);
    pendingRun.current = undefined;
  };

  const endSession = (error: AuthError) => {
    showChallengeError(error);
    close();
    logout();
  };

  // Step A. From the dialog (a code that can no longer be resent) the error
  // stays under the input; from the export button it is a toast.
  const sendCode = () => {
    if (startMutation.isPending) return;

    startMutation.mutate(undefined, {
      onSuccess: response => {
        if (!response?.data) {
          toast.error('Something went wrong');
          return;
        }
        setChallenge(toExportChallenge(response.data));
        setError(undefined);
      },
      onError: (error: AuthError) => {
        const message = error?.message ?? 'Something went wrong';
        if (challenge) setError(message);
        else toast.error(message);
      },
    });
  };

  const confirm = (run: (token: string) => void) => {
    pendingRun.current = run;
    sendCode();
  };

  const verify = (code: string) => {
    if (!challenge || verifyMutation.isPending) return;

    verifyMutation.mutate(
      { challenge_id: challenge.challengeId, code },
      {
        onSuccess: response => {
          const token = response?.data?.export_token;
          if (!token) {
            setError('Something went wrong');
            return;
          }

          saveToken(token, response.data?.expires_in ?? 0);
          const run = pendingRun.current;
          close();
          run?.(token);
        },
        onError: (error: AuthError) => {
          // 403: suspended after too many wrong codes, and the session with it.
          if (isSuspended(error) || isSessionExpired(error)) {
            endSession(error);
            return;
          }
          setError(error?.message ?? 'Something went wrong');
        },
      }
    );
  };

  const resend = () => {
    if (!challenge || resendMutation.isPending) return;

    resendMutation.mutate(
      { challenge_id: challenge.challengeId },
      {
        onSuccess: response => {
          setChallenge({
            ...challenge,
            sentAt: Date.now(),
            resendAfter: response?.data?.resend_after ?? challenge.resendAfter,
          });
          setError(undefined);
          toast.success(response?.meta.message);
        },
        onError: (error: AuthError) => {
          if (isSessionExpired(error)) {
            endSession(error);
            return;
          }
          // 401: the code expired. 429: out of resends (the button waits out
          // the cooldown). Either way this challenge is done: start a new one.
          if (error?.status === 401 || error?.status === 429) {
            sendCode();
            return;
          }
          setError(error?.message ?? 'Something went wrong');
        },
      }
    );
  };

  return {
    challenge,
    error,
    confirm,
    verify,
    resend,
    close,
    clearError: () => setError(undefined),
    isSending: startMutation.isPending || resendMutation.isPending,
    isVerifying: verifyMutation.isPending,
  };
};

type ExportConfirmation = ReturnType<typeof useExportConfirmation>;

/**
 * The set-password page: trades the one-time token from a login with the
 * temporary password for the user's own password, then finishes the login
 * the same way the login form does (straight in, or on to the email code).
 */
const useSetPassword = (setup: PendingPasswordSetup | null) => {
  const navigate = useNavigate();
  const completeLogin = useCompleteLogin();
  const startTwoFactor = useStartTwoFactor();
  const startTotp = useStartTotp();

  const setPasswordMutation = useMutation({
    mutationFn: AuthService.setPassword,
  });

  const backToLogin = () => {
    clearPendingPasswordSetup();
    navigate('/', { replace: true });
  };

  const setPassword = (values: InferType<typeof setPasswordSchema>) => {
    if (!setup || setPasswordMutation.isPending) return;

    const trustedDeviceToken = getTrustedDeviceToken(setup.email);

    setPasswordMutation.mutate(
      {
        password_change_token: setup.token,
        password: values.password,
        passConfirmation: values.passConfirmation,
        trusted_device_token: trustedDeviceToken,
      },
      {
        onSuccess: response => {
          const result = response?.data;
          clearPendingPasswordSetup();

          if (
            result &&
            ('requires_totp_setup' in result || 'requires_totp' in result)
          ) {
            toast.success(response?.meta.message);
            startTotp(result);
            return;
          }

          if (result && 'requires_2fa' in result) {
            toast.success(response?.meta.message);
            startTwoFactor(result, setup.email, trustedDeviceToken);
            return;
          }

          completeLogin(
            result && 'access_token' in result
              ? result.access_token
              : undefined,
            response?.meta.message
          );
        },
        onError: (error: AuthError) => {
          toast.error(error?.message ?? 'Something went wrong');
          // 401: the one-time token is used up or expired; log in again.
          if (error?.status === 401) backToLogin();
        },
      }
    );
  };

  return { setPassword, backToLogin, setPasswordMutation };
};

export type { ExportChallenge, ExportConfirmation };
export {
  useAuth,
  useCompleteLogin,
  useResetSessionState,
  useTwoFactor,
  useSetPassword,
  useExportConfirmation,
  TWO_FACTOR_PATH,
  SET_PASSWORD_PATH,
};
